import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, UseFormSetValue, UseFormWatch, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { DataTable, type DataTableColumnDef } from '@column/ui';

import { Asterix } from '../_components/Asterix';
import { AuthorizationCell } from '../_components/AuthorizationCell';
import { CounterCell } from '../_components/CounterCell';
import { ReviewFormValues, TransferState, TransferTemplateForm } from '../types';
import { BankAccountCell, FormCell, StateCell, StateCellOption } from '~/components/Table';
import { useCreateBookTransfer } from '~/hooks/useTransfers';
import { useUpdateTransferTemplate } from '~/hooks/useTransferTemplates';
import { Action } from '~/lib/authz';
import { BookTransferTemplateResponse } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { buildBookTransferUpdateRequest } from './api';
import { getColumnVisibility } from './shared';

export const defaultBookColumns = ['amount', 'senderBankAccountId', 'receiverBankAccountId', 'description'];

export const isBookTransferAuthorized = (
  row: BookTransferTemplateResponse,
  isAuthorized: (action: Action) => boolean
) => {
  return row.senderBankAccountId
    ? isAuthorized({
        permission: 'bookTransfers',
        permissionLevel: 'write',
        bankAccount: { id: row.senderBankAccountId },
      })
    : true;
};

export const buildBookEditColumns = (
  setValue: UseFormSetValue<TransferTemplateForm>
): DataTableColumnDef<BookTransferTemplateResponse & { isAuthorized?: boolean }>[] => [
  {
    accessor: 'isAuthorized',
    header: '',
    cell: ({ value }) => <AuthorizationCell isAuthorized={value} />,
    width: '32px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`bookTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable
            currency="USD"
            value={value}
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'senderBankAccountId',
    header: (
      <>
        Sender Bank Account
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Sender Bank Account',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`bookTransfers.${rowIndex}.senderBankAccountId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <BankAccountCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`bookTransfers.${rowIndex}.senderBankAccountDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
  },
  {
    accessor: 'receiverBankAccountId',
    header: (
      <>
        Receiver Bank Account
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Receiver Bank Account',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`bookTransfers.${rowIndex}.receiverBankAccountId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <BankAccountCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`bookTransfers.${rowIndex}.receiverBankAccountDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`bookTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell isEditable value={value} placeholder="Description" onValueChange={onChange} />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 2fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`bookTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable
            placeholder="Please select"
            value={String(typeof value !== 'boolean' ? true : value)}
            onValueChange={(newValue) => onChange(newValue === 'true')}
            options={[
              { label: 'Allow', value: 'true' },
              { label: 'Deny', value: 'false' },
            ]}
          />
        )}
      </FormCell>
    ),
    width: '148px',
  },
];

export const bookReviewColumns: DataTableColumnDef<
  Partial<BookTransferTemplateResponse> & { id: string; state: TransferState }
>[] = [
  {
    accessor: 'id',
    header: '',
    cell: ({ rowIndex }) => <CounterCell value={rowIndex + 1} />,
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'state',
    header: '',
    cell: ({ value }) => (
      <StateCell state={value.type as StateCellOption} message={value.type === 'error' ? value.message : undefined} />
    ),
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`bookTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable={row.state.type === 'error'}
            value={value as number | undefined}
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
            currency="USD"
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'senderBankAccountId',
    header: 'Sender Bank Account',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.senderBankAccountDescription ? (
        <DataTable.TextCell value={row.senderBankAccountDescription} />
      ) : (
        <FormCell id={`bookTransfers.${rowIndex}.senderBankAccountId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <BankAccountCell
              isEditable={row.state.type === 'error'}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v.id)}
            />
          )}
        </FormCell>
      ),
    width: 'minmax(200px, 1fr)',
  },
  {
    accessor: 'receiverBankAccountId',
    header: 'Receiver Bank Account',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.receiverBankAccountDescription ? (
        <DataTable.TextCell value={row.receiverBankAccountDescription} />
      ) : (
        <FormCell id={`bookTransfers.${rowIndex}.receiverBankAccountId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <BankAccountCell
              isEditable={row.state.type === 'error'}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v.id)}
            />
          )}
        </FormCell>
      ),
    width: 'minmax(200px, 1fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`bookTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Description"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 1fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`bookTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable={row.state.type === 'error'}
            value={String((value as boolean) ?? false)}
            onValueChange={(v) => onChange(v === 'true')}
            placeholder="Please select"
            options={['Allow', 'Deny'].map((code: string) => ({
              label: code,
              value: String(code === 'Allow'),
            }))}
          />
        )}
      </FormCell>
    ),
    width: '144px',
  },
];

export const useBookTransfersEditTable = () => {
  const { isAuthorized } = useSessionStore();
  const { control, setValue, watch, resetField } = useFormContext<TransferTemplateForm>();
  const bookTransfers = watch('bookTransfers');
  const { append, remove } = useFieldArray({
    control,
    name: 'bookTransfers',
  });
  const bookTransfersWithPermission = useMemo(
    () =>
      (bookTransfers ?? []).map((transfer) => ({
        ...transfer,
        isAuthorized: isBookTransferAuthorized(transfer, isAuthorized),
      })),
    [JSON.stringify(bookTransfers), isAuthorized]
  );
  const allBookTransfersAuthorized = bookTransfersWithPermission.every((transfer) => transfer.isAuthorized);
  const bookColumns = useMemo(() => buildBookEditColumns(setValue), [setValue]);

  const visibleColumns = watch('visibleColumns');
  const bookColumnVisibility = useMemo(
    () => ({
      ...getColumnVisibility(bookColumns, visibleColumns),
      isAuthorized: !allBookTransfersAuthorized,
    }),
    [bookColumns, allBookTransfersAuthorized, visibleColumns]
  );

  const bookAppend = useCallback(() => {
    append({
      transferTemplateRowId: uuid(),
      amount: undefined,
      senderBankAccountId: watch('bookDefaultParameters.senderBankAccountId'),
      receiverBankAccountId: watch('bookDefaultParameters.receiverBankAccountId'),
      description: watch('bookDefaultParameters.description'),
      allowOverdraft: true,
    });
  }, [append, watch]);

  const bookRemove = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }
      const index = bookTransfers?.findIndex((transfer) => transfer.transferTemplateRowId === id) ?? -1;
      if (index === -1) {
        return;
      }
      remove(index);
      resetField('bookTransfers', {
        defaultValue: (bookTransfers ?? []).filter((_, i) => i !== index),
        keepDirty: true,
        keepError: true,
        keepTouched: true,
      });
    },
    [remove, resetField, bookTransfers]
  );

  return {
    append: bookAppend,
    remove: bookRemove,
    data: bookTransfersWithPermission,
    columns: bookColumns,
    columnVisibility: bookColumnVisibility,
  };
};

export const useBookTransfersReviewTable = ({
  control,
  watch,
  transferTemplateId,
}: {
  control: Control<ReviewFormValues>;
  watch: UseFormWatch<ReviewFormValues>;
  transferTemplateId: string;
}) => {
  const { fields: transfers, update } = useFieldArray({ control, name: 'bookTransfers' });
  const [states, setStates] = useState<TransferState[]>([]);
  const { addDangerNotification } = useNotificationStore();
  const { createRequest: createBookTransfer } = useCreateBookTransfer();
  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate();

  useEffect(() => {
    setStates((prev) =>
      prev.length === transfers.length ? prev : Array.from({ length: transfers.length }, () => ({ type: 'idle' }))
    );
  }, [transfers.length]);

  const setState = useCallback((index: number, state: TransferState) => {
    setStates((prev) => prev.map((s, i) => (i === index ? state : s)));
  }, []);

  const handleRow = useCallback(
    async (index: number): Promise<boolean> => {
      const currentState = states[index] || { type: 'idle' };
      if (currentState.type === 'success') return false;

      setState(index, { type: 'loading' });
      try {
        const bookData = watch(`bookTransfers.${index}`) as BookTransferTemplateResponse;
        const idempotencyKeyValue = uuid();

        await createBookTransfer({
          amount: `${bookData.amount}`,
          senderBankAccountId: bookData.senderBankAccountId,
          receiverBankAccountId: bookData.receiverBankAccountId,
          description: bookData.description,
          allowOverdraft: bookData.allowOverdraft,
          currencyCode: 'USD',
          idempotencyKey: idempotencyKeyValue,
        });

        setState(index, { type: 'success' });
        return true;
      } catch (e: any) {
        const errorMessage = e?.message ? `${e.message} ${e?.details?.reason ?? ''}` : 'Unknown error occurred';
        setState(index, { type: 'error', message: errorMessage });
        addDangerNotification({
          content: (
            <>
              <span className="underline">{index + 1}. Transfer</span> {errorMessage}
            </>
          ),
          timeout: 8000,
        });
        return false;
      }
    },
    [states, setState, watch, createBookTransfer, update, addDangerNotification]
  );

  const applyErrorCorrectionsAndUpdateTemplate = useCallback(async () => {
    const curData = watch('bookTransfers');
    const updateRequest = buildBookTransferUpdateRequest(curData);
    const updatedTemplate = await updateTransferTemplate({
      transferTemplateId,
      bookTransfers: updateRequest,
    });
    if (!updatedTemplate) return [] as BookTransferTemplateResponse[];

    const updatedBookTransfers = updatedTemplate.bookTransfers || [];
    updatedBookTransfers.forEach((updatedTransfer: BookTransferTemplateResponse, i: number) => {
      const originalTransfer = curData[i];
      update(i, {
        ...originalTransfer,
        senderBankAccountDescription: updatedTransfer.senderBankAccountDescription,
        receiverBankAccountDescription: updatedTransfer.receiverBankAccountDescription,
      });
    });
    return updatedBookTransfers as BookTransferTemplateResponse[];
  }, [watch, transferTemplateId, updateTransferTemplate, update]);

  const visibleColumns = useWatch({ control, name: 'visibleColumns' });
  const bookColumnVisibility = useMemo(
    () => ({ ...getColumnVisibility(bookReviewColumns, visibleColumns), id: true, state: true }),
    [visibleColumns]
  );

  const data = useMemo(
    () =>
      transfers.map((transfer, i) => ({
        ...transfer,
        id: transfer.id ?? String(i),
        state: states[i] ?? ({ type: 'idle' } as TransferState),
      })),
    [transfers, states]
  );

  return {
    columns: bookReviewColumns,
    columnVisibility: bookColumnVisibility,
    data,
    states,
    handleRow,
    applyErrorCorrectionsAndUpdateTemplate,
  };
};
